import { FuuzeColors } from '@fuuze/shared';
import debounce from 'lodash.debounce';
import {
  type CSSProperties,
  Fragment,
  type PropsWithChildren,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import Marquee from 'react-fast-marquee';
import styled from 'styled-components';
import { v4 } from 'uuid';

type FuuzeMarqueeProps = PropsWithChildren<{
  play: boolean;
  delay?: number;
  speed?: number;
  style?: CSSProperties;
}>;

export function PlayerTitleMarquee(props: FuuzeMarqueeProps) {
  const [key, setKey] = useState(useId());

  // Reset the key of the component to force it to rerender
  const debounceWindowListener = useMemo(() => {
    return debounce(() => {
      setKey(v4());
    }, 300);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', debounceWindowListener);

    return () => {
      window.removeEventListener('resize', debounceWindowListener);
      debounceWindowListener.cancel();
    };
  }, [debounceWindowListener]);

  return (
    <Fragment key={key}>
      <PlayerTitleMarqueeContent {...props} />
    </Fragment>
  );
}

function PlayerTitleMarqueeContent({ play, children, speed, delay = 0, style }: FuuzeMarqueeProps) {
  const ref = useRef(null);
  const [_play, setPlay] = useState(false);
  const [firstDelayPassed, setFirstDelayPassed] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPlay(play);
      setFirstDelayPassed(true);
    }, delay * 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!firstDelayPassed) return;
    setPlay(play);
  }, [play, firstDelayPassed]);

  const onCycleComplete = () => {
    if (!play) return;
    setPlay(false);
    setTimeout(() => {
      setPlay(true);
    }, delay * 1000);
  };

  return (
    <MarqueeStyled
      ref={ref}
      style={style}
      gradient
      $play={_play}
      play={_play}
      pauseOnHover
      gradientColor={FuuzeColors.background}
      gradientWidth={10}
      loop={0}
      onCycleComplete={onCycleComplete}
      speed={speed}
    >
      {children}
    </MarqueeStyled>
  );
}

const MarqueeStyled = styled(Marquee)<{ $play: boolean }>`
  .rfm-overlay::before, .rfm-overlay::after {
    display: none;
  }
`;
